/**
 * D4 (B7) 잡음 바닥 추정 — 순수 함수.
 *
 * 무엇을 세는가: **비발화 블록의 하위 10 백분위 dBFS**. 주변 잡음이 크면
 * 전사가 그 잡음까지 발화로 읽어 엉뚱한 자막을 만들고, 사용자에게는 원인이
 * 보이지 않는다. 바닥을 추정해 한 줄로 알려 주면 그 원인이 드러난다.
 *
 * **분모 규율(CLAUDE.md §2.1)**: 분모는 `dbfs <= NOISE_SPEECH_EXCLUDE_DBFS`
 * (−35) 인 블록뿐이고 **큰 발화 블록은 분모 밖**이다. 발화를 섞으면 말하는
 * 사람이 곧 "잡음이 크다" 로 집계된다. 이 추정기가 모르는 실패(발화 구간에만
 * 있는 잡음)는 0 으로 세지는 것이지 없는 것이 아니다.
 *
 * **왜 zone 이 아니라 에너지 상한인가(설계 CORRECTION #1)**: 잡음 바닥은
 * 정의상 VAD 바닥 **위**에 있다. `levelZone` 의 `low` 는 `dbfs < floor`
 * (기본 −55)이므로 zone 으로 표본을 뽑으면 표본이 전부 −55 미만이 되고,
 * p10 은 진입 문턱 −50 에 **절대 못 미친다** — 기본 설정에서 경고가 도달
 * 불가였다(codex 검토 3건). 그래서 큰 발화만 상한으로 제외하고, 조용한
 * 구간을 고르는 일은 p10 에 맡긴다.
 *
 * 발화 제외 불변식은 **정상성 가드**가 대신 지킨다: 정상 잡음 바닥은 std
 * 1~2 dB(연구 §0.1 실측)이고 발화 블록은 10 dB+ 진동하므로, 조용한 화자
 * (−38±8)가 쉼 없이 말해도 **절단 전** `p75 − p10` 이 커서 경고가 안 난다.
 *
 * **FP 0 이 목표다**(§1-2): 이 안내가 정상 조건에서 뜨면 배지 전체가 마모돼
 * 진짜 경고도 같이 안 보인다. 그래서 방어가 네 겹이다 —
 *   (i)  진입 문턱이 건강군(연구 §0.1 실측 −74~−78)에서 24 dB 떨어져 있다
 *   (ii) 10 s 지속을 요구한다
 *   (iii) 표본은 비발화(−35 이하) 블록만, 최소 40개 + 정상성 가드
 *   (iv) 문구는 호스트에게만 보인다(뷰어는 마이크가 없다)
 * 그래도 실회의 FP 는 **미측정**이다(표본이 n=6 파형 역추정) — 실회의 1회
 * 관측 전까지 문턱을 조이지 않는다.
 *
 * 문턱 재조정이 한 줄이 되도록 상수는 **이 파일 최상단 한 곳**에 둔다.
 * 워크릿에서 import 하지 않는다(DOM·시간 의존 0 이지만 워크릿 경로 밖이다).
 */

/** 슬라이딩 창 — 이 안의 표본만 본다. */
export const NOISE_WINDOW_MS = 30_000
/** 하위 백분위 — 바닥은 평균이 아니라 조용한 쪽 꼬리다. */
export const NOISE_PCTL = 0.1
/** 이 미만이면 추정하지 않는다(정보가 없으면 경고도 없다). */
export const NOISE_MIN_SAMPLES = 40
/** 진입 — 바닥이 이보다 크면 잡음이 크다. */
export const NOISE_ENTER_DBFS = -50
/** 해제 — 히스테리시스 6 dB. */
export const NOISE_EXIT_DBFS = -56
/** 진입에 요구하는 지속 시간. */
export const NOISE_SUSTAIN_MS = 10_000
/**
 * 표본 상한 — 이보다 큰 블록은 발화로 보고 분모에서 뺀다.
 *
 * zone 대용이 아니다(그 연결이 F3 의 결함이었다). 잡음 바닥은 VAD 바닥
 * 위에 있을 수 있으므로 `low` 로는 못 뽑는다 — 큰 발화만 자르고 조용한
 * 구간 선택은 p10 에 맡긴다.
 */
export const NOISE_SPEECH_EXCLUDE_DBFS = -35
/**
 * 정상성 가드 — `p75 − p10` 이 이보다 크면 잡음 바닥이 아니라 **발화**다.
 *
 * 근거: 연구 §0.1 실측 — 정상 잡음 바닥 std 1~2 dB, 발화 블록 10 dB+.
 * 이 가드가 발화 제외 불변식을 지킨다(상한만으로는 조용한 화자를 못 가른다).
 *
 * **절단 전 표본으로 잰다(R1)**: 상한(−35)으로 절단한 표본에서 재면 조용한
 * 화자 U[−46,−30] 의 모집단이 U[−46,−35] 로 접혀 spread 가 4.4 dB 로
 * **압축**되고, 가드를 통과해 200 seed 중 159 가 경고했다(codex 2차 R1).
 * 절단 전에서는 화자 10.4 dB / 팬 U[−44,−42] 1.3 dB 라 6 dB 문턱이 둘을
 * 확실히 가른다. 바닥 추정(p10)은 그대로 절단 표본이 쓴다 — 거기서는 발화가
 * 분모를 오염시키므로 절단이 맞다.
 */
export const NOISE_STATIONARY_SPREAD_DB = 6

export interface NoiseFloorSample {
  t: number
  dbfs: number
}

export interface NoiseFloorState {
  /** 창 안의 **비발화**(−35 이하) 블록 표본 — 바닥(p10) 추정용 */
  samples: readonly NoiseFloorSample[]
  /**
   * 창 안의 **절단 전** 활성 블록 표본 — 정상성(p75 − p10) 전용(R1).
   * 같은 30 s 창·같은 신규 프레임 규칙으로 유지한다.
   */
  allSamples: readonly NoiseFloorSample[]
  /** 추정 바닥(dBFS). 표본이 모자라면 null — 모르는 것을 지어내지 않는다. */
  floorDbfs: number | null
  warn: boolean
  /** 진입 문턱 위로 처음 올라간 시각(ms). 아래면 null */
  since: number | null
}

export const initialNoiseFloorState: NoiseFloorState = {
  samples: [],
  allSamples: [],
  floorDbfs: null,
  warn: false,
  since: null,
}

/** 정렬된 표본의 p10 — 선형 보간 없이 인덱스 절단(표본이 40+ 라 충분하다). */
function percentile(sorted: readonly number[], p: number): number {
  const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * p))
  return sorted[idx] as number
}

/**
 * 상태를 한 걸음 갱신한다(순수 함수 — 테스트가 여기를 겨눈다).
 *
 * `sample` 은 **선택**이다: 새 오디오 블록이 왔을 때만 넘긴다. 없이 부르면
 * 창 만료와 경고 시간 평가만 한다(F4).
 *
 * `warn` 갱신은 `nextLowLevelState`(resampler.ts)와 **같은 형태**다:
 * 해제는 즉시, 진입은 지속 시간을 요구하고, 그 사이 구간은 현 상태 유지.
 *
 * `active=false`(캡처 중지·뮤트)면 전부 리셋한다 — 꺼진 마이크에 "주변
 * 잡음이 큽니다" 는 틀린 말이다.
 */
export function pushNoiseSample(
  prev: NoiseFloorState,
  now: number,
  active: boolean,
  sample?: { dbfs: number },
): NoiseFloorState {
  if (!active) return initialNoiseFloorState

  // 큰 발화 블록은 분모 밖이다 — 말하는 사람을 잡음으로 세지 않는다.
  // `sample` 이 없으면 **창 만료와 시간 판정만** 한다(F4: 타이머가 옛
  // 측정값을 새 표본으로 복제하면 새 오디오 없이도 표본 40·지속 10 s 가
  // 채워져 "표본 부족 시 경고 금지" 가 무너진다).
  const sampled = sample !== undefined
    && sample.dbfs <= NOISE_SPEECH_EXCLUDE_DBFS
  const kept = prev.samples.filter((s) => now - s.t <= NOISE_WINDOW_MS)
  const samples = sampled
    ? [...kept, { t: now, dbfs: (sample as { dbfs: number }).dbfs }]
    : kept
  // 정상성 전용 표본은 **절단하지 않는다**(R1) — 절단이 spread 를 압축해
  // 조용한 화자를 잡음처럼 보이게 만든다. 창·신규 프레임 규칙은 동일하다.
  const keptAll = prev.allSamples.filter((s) => now - s.t <= NOISE_WINDOW_MS)
  const allSamples = sample !== undefined
    ? [...keptAll, { t: now, dbfs: sample.dbfs }]
    : keptAll

  if (samples.length < NOISE_MIN_SAMPLES) {
    // 정보가 없으면 경고하지 않는다(거짓 경고는 진짜 경고를 죽인다).
    return { samples, allSamples, floorDbfs: null, warn: false, since: null }
  }

  const sorted = samples.map((s) => s.dbfs).sort((a, b) => a - b)
  const floorDbfs = percentile(sorted, NOISE_PCTL)
  // 정상성 — 발화가 섞이면 상위 백분위가 p10 보다 훨씬 위에 있다. 절단 전
  // 표본에서 p75 − p10 을 본다(절단하면 발화의 윗꼬리가 통째로 사라진다).
  const sortedAll = allSamples.map((s) => s.dbfs).sort((a, b) => a - b)
  const stationary = percentile(sortedAll, 0.75) - percentile(sortedAll, NOISE_PCTL)
    <= NOISE_STATIONARY_SPREAD_DB

  // 해제는 즉시 — 조용해졌는데 안내가 남으면 거짓 정보다.
  if (floorDbfs < NOISE_EXIT_DBFS) {
    return { samples, allSamples, floorDbfs, warn: false, since: null }
  }
  // 진입 문턱 아래(해제 문턱과의 사이).
  if (floorDbfs < NOISE_ENTER_DBFS) {
    // F5: 해제 히스테리시스는 **이미 켜진 경고에만** 적용한다. warn=false
    // 인데 since 를 보존하면 진입 조건이 중단됐는데도 타이머가 살아남아,
    // 잠깐 −43 → 11 s 동안 −53 → −43 복귀에서 즉시 경고한다(FP).
    if (!prev.warn) {
      return { samples, allSamples, floorDbfs, warn: false, since: null }
    }
    return { ...prev, samples, allSamples, floorDbfs }
  }
  // 진입 문턱 위지만 정상 잡음 바닥이 아니면(발화 진동) 경고하지 않는다.
  if (!stationary) {
    return { samples, allSamples, floorDbfs, warn: false, since: null }
  }
  // 진입 문턱 위 — 지속 시간을 채워야 안내를 켠다.
  const since = prev.since ?? now
  return {
    samples,
    allSamples,
    floorDbfs,
    warn: prev.warn || now - since >= NOISE_SUSTAIN_MS,
    since,
  }
}
